import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2, Trophy, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_STYLES = {
  legendary: { bg: 'from-[#1a1a2e] via-[#16213e] to-[#0f3460]', glow: '#ffd700', label: 'LEGENDARY' },
  epic: { bg: 'from-[#1a1a2e] via-[#2d1b4e] to-[#1a0a3e]', glow: '#a855f7', label: 'EPIC' },
  rare: { bg: 'from-[#0c1929] via-[#1a2744] to-[#0c1929]', glow: '#3b82f6', label: 'RARE' },
  uncommon: { bg: 'from-[#111827] via-[#1f2937] to-[#111827]', glow: '#10b981', label: 'UNCOMMON' },
  common: { bg: 'from-[#18181b] via-[#27272a] to-[#18181b]', glow: '#a1a1aa', label: 'COMMON' },
};

const BadgeSharePage = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/badges/share/${shareId}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-center p-6">
        <div>
          <Trophy className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Badge introuvable</h1>
          <p className="text-zinc-400 mb-6">Ce badge n'existe pas ou a expiré.</p>
          <a href="/">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Découvrir YUMIX
            </Button>
          </a>
        </div>
      </div>
    );
  }

  const rarity = data.badge_rarity || 'common';
  const style = RARITY_STYLES[rarity] || RARITY_STYLES.common;
  const dateStr = data.earned_at
    ? new Date(data.earned_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="badge-share-page">
      {/* Nav */}
      <nav className="p-4 flex items-center justify-between border-b border-zinc-800">
        <a href="/" className="flex items-center gap-2">
          <span className="font-black text-xl tracking-wider text-white uppercase">YUMIX</span>
        </a>
        <a href="/">
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:text-white">
            S'inscrire gratuitement
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </a>
      </nav>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center">
          {/* Card */}
          <div className={`bg-gradient-to-br ${style.bg} rounded-2xl p-8 mb-8 border border-zinc-800 relative overflow-hidden`}>
            {/* Ambient glow */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-[80px] opacity-20"
              style={{ background: style.glow }}
            />

            <div className="relative z-10">
              {/* Rarity label */}
              <div className="mb-6">
                <span
                  className="text-[10px] font-bold tracking-[3px] uppercase px-3 py-1 rounded border"
                  style={{ color: style.glow, borderColor: `${style.glow}40` }}
                >
                  {style.label}
                </span>
              </div>

              {/* Badge icon */}
              <div
                className="w-24 h-24 rounded-full mx-auto mb-5 flex items-center justify-center text-5xl border-2"
                style={{ background: `${data.badge_color}15`, borderColor: `${data.badge_color}50` }}
              >
                {data.badge_icon}
              </div>

              {/* Badge name */}
              <h1 className="text-3xl font-black text-white uppercase tracking-wide mb-2">
                {data.badge_name}
              </h1>
              <p className="text-zinc-400 text-sm mb-6">
                {data.badge_description}
              </p>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-zinc-600 to-transparent mb-6" />

              {/* User */}
              <div className="flex items-center justify-center gap-4">
                {data.user_picture && (
                  <img
                    src={data.user_picture.startsWith('/') ? `${BACKEND_URL}${data.user_picture}` : data.user_picture}
                    alt=""
                    className="w-10 h-10 rounded-full border border-zinc-600"
                  />
                )}
                <div className="text-left">
                  <p className="text-white font-semibold">{data.user_name}</p>
                  <p className="text-zinc-500 text-xs">{dateStr}</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white">
              Ton coach privé et chef, dans ta poche.
            </h2>
            <p className="text-zinc-400 text-sm">
              YUMIX analyse tes repas, traque les aubaines et te coach de vive voix.
              Rejoins {data.user_name?.split(' ')[0] || 'la communauté'} gratuitement.
            </p>
            <a href="/" data-testid="share-cta-btn">
              <Button className="bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold px-8 py-3 text-lg h-auto hover:opacity-90 transition-opacity">
                Commencer l'expérience
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <p className="text-zinc-600 text-xs">Connexion avec Google — 100% Gratuit</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center border-t border-zinc-800">
        <p className="text-zinc-600 text-xs">extramiles.ca — YUMIX Coaching</p>
      </footer>
    </div>
  );
};

export default BadgeSharePage;
